import { Col, Form, InputNumber, Row } from 'antd';
import { FormikProps } from 'formik';
import { memo } from 'react';

import MUpload from '@Components/Base/Upload/Upload';
import { MDatePicker, MInput, MSelect, MTextArea } from '@Components/Base';
import { Option } from '@Components/types/options';
import { RcCustomRequestOptions } from '@Components/types/UploadType';
import { SponsorTypeCodes } from '@Enum/api/SponsorTypeCodes.enum';
import dayjs from 'dayjs';
import { Attachment } from '@Components/types/Attachment';
import { prepareDatePickerValue } from '@Utils/transformDateTime';

import { OverseasMissionFormValues } from '../types';

interface OverseasDetailsSectionProps {
  formik: FormikProps<OverseasMissionFormValues>;
  label: string;
  isApprovalForm?: boolean;
  leaveTypeOptions: Option[];
  sponsorOptions: Option[];
  companyOptions: Option[];
  onFileRequest?: (options: RcCustomRequestOptions) => void;
  onFileRemove?: () => void;
  uploadedFile?: Attachment;
  onUploadedFileRemove?: (value: boolean | undefined) => void;
}

const OverseasDetailsSection: React.FC<OverseasDetailsSectionProps> = ({
  formik,
  isApprovalForm = false,
  leaveTypeOptions,
  sponsorOptions,
  companyOptions,
  onFileRequest,
  onFileRemove,
  uploadedFile,
  onUploadedFileRemove,
}) => {
  const {
    employeeNumber: employeeNumberTouched,
    employeeName: employeeNameTouched,
    placeOfTravel: placeOfTravelTouched,
    dateOfTravel: dateOfTravelTouched,
    returnDate: returnDateTouched,
    purposeOfTravel: purposeOfTravelTouched,
    approvedTravelAmount: approvedTravelAmountTouched,
    country: countryTouched,
    sponsoredBy: sponsoredByTouched,
    sponsoredByExtra: sponsoredByExtraTouched,
  } = formik.touched;
  const {
    employeeNumber: employeeNumberError,
    employeeName: employeeNameError,
    placeOfTravel: placeOfTravelError,
    dateOfTravel: dateOfTravelError,
    returnDate: returnDateError,
    purposeOfTravel: purposeOfTravelError,
    approvedTravelAmount: approvedTravelAmountError,
    country: countryError,
    sponsoredBy: sponsoredByError,
    sponsoredByExtra: sponsoredByExtraError,
  } = formik.errors;

  return (
    <Row>
      <Col span={24}>
        <Form.Item
          label="Employee Number"
          validateStatus={
            employeeNumberError && employeeNumberTouched ? 'error' : ''
          }
          help={
            employeeNumberError && employeeNumberTouched
              ? employeeNumberError
              : ''
          }
        >
          <MInput
            name="employeeNumber"
            placeholder="Employee number"
            disabled
            value={formik.values.employeeNumber}
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item
          label="Employee Name"
          validateStatus={
            employeeNameError && employeeNameTouched ? 'error' : ''
          }
          help={
            employeeNameError && employeeNameTouched ? employeeNameError : ''
          }
        >
          <MInput
            name="employeeName"
            placeholder="Employee name"
            disabled
            value={formik.values.employeeName}
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item label="Designation">
          <MInput
            name="designation"
            placeholder="Employee Designation"
            value={formik.values.designation}
            disabled
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item label="Current Location">
          <MInput
            name="currentLocation"
            placeholder="Employee Current Location"
            value={formik.values.currentLocation}
            disabled
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item
          label="Place of Travel"
          validateStatus={
            placeOfTravelError && placeOfTravelTouched ? 'error' : ''
          }
          help={
            placeOfTravelError && placeOfTravelTouched ? placeOfTravelError : ''
          }
        >
          <MInput
            name="placeOfTravel"
            placeholder="Enter place of travel"
            value={formik.values.placeOfTravel}
            disabled={isApprovalForm}
            onChange={(value) =>
              formik.setFieldValue('placeOfTravel', value.target.value)
            }
            onBlur={() => {
              formik.setFieldTouched('placeOfTravel', true);
            }}
            maxLength={50}
          />
        </Form.Item>
      </Col>
      <Col span={12}>
        <Form.Item
          label="Date of Travel"
          validateStatus={
            dateOfTravelError && dateOfTravelTouched ? 'error' : ''
          }
          help={
            dateOfTravelError && dateOfTravelTouched ? dateOfTravelError : ''
          }
        >
          <MDatePicker
            className="leave-deatails-datepicker"
            name="dateOfTravel"
            value={
              formik.values.dateOfTravel &&
              dayjs(formik.values.dateOfTravel).tz('UTC')
            }
            format="DD/MM/YYYY"
            onChange={(value) =>
              formik.setFieldValue(
                'dateOfTravel',
                prepareDatePickerValue(value, true)
              )
            }
            onBlur={() => {
              formik.setFieldTouched('dateOfTravel', true);
            }}
            disabled={isApprovalForm}
          />
        </Form.Item>
      </Col>
      <Col span={11} push={1}>
        <Form.Item
          label="Return Date"
          validateStatus={returnDateError && returnDateTouched ? 'error' : ''}
          help={returnDateError && returnDateTouched ? returnDateError : ''}
        >
          <MDatePicker
            className="leave-deatails-datepicker"
            name="returnDate"
            value={
              formik.values.returnDate &&
              dayjs(formik.values.returnDate).tz('UTC')
            }
            format="DD/MM/YYYY"
            onChange={(value) =>
              formik.setFieldValue(
                'returnDate',
                prepareDatePickerValue(value, true)
              )
            }
            onBlur={() => {
              formik.setFieldTouched('returnDate', true);
            }}
            disabledDate={(current) => {
              if (formik.values.dateOfTravel) {
                // disable days less than chosen dateOfTravel
                return (
                  current &&
                  dayjs(current).endOf('day') <
                    dayjs(formik.values.dateOfTravel)
                );
              }

              return false;
            }}
            disabled={isApprovalForm}
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item label="Total No. of days">
          <MInput
            className="leave-details-input"
            name="totDays"
            placeholder="Total No. of days"
            value={formik.values.totDays}
            disabled
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item
          label="Purpose of Travel"
          validateStatus={
            purposeOfTravelError && purposeOfTravelTouched ? 'error' : ''
          }
          help={
            purposeOfTravelError && purposeOfTravelTouched
              ? purposeOfTravelError
              : ''
          }
        >
          <MTextArea
            name="purposeOfTravel"
            placeholder="Leave a purpose"
            value={formik.values.purposeOfTravel}
            onChange={formik.handleChange}
            onBlur={() => {
              formik.setFieldTouched('purposeOfTravel', true);
            }}
            rows={4}
            showCount
            maxLength={100}
            disabled={isApprovalForm}
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item
          label="Approved Travel Amount"
          validateStatus={
            approvedTravelAmountError && approvedTravelAmountTouched
              ? 'error'
              : ''
          }
          help={
            approvedTravelAmountError && approvedTravelAmountTouched
              ? approvedTravelAmountError
              : ''
          }
        >
          <InputNumber
            className="trojan-input leave-details-input"
            name="approvedTravelAmount"
            placeholder="Enter travel amount"
            value={formik.values.approvedTravelAmount}
            onChange={(value) => {
              formik.setFieldValue('approvedTravelAmount', value);
            }}
            precision={2}
            onBlur={() => {
              formik.setFieldTouched('approvedTravelAmount', true);
            }}
            disabled={isApprovalForm}
            controls={false}
            max={1e15}
          />
        </Form.Item>
      </Col>
      {!isApprovalForm && (
        <Col span={24}>
          <Form.Item label="Attachment">
            <MUpload
              onFileRequest={onFileRequest}
              onFileRemove={onFileRemove}
              disabled={isApprovalForm}
              uploadedFile={uploadedFile}
              onUploadedFileRemove={onUploadedFileRemove}
            />
          </Form.Item>
        </Col>
      )}
      <Col span={24}>
        <Form.Item
          label="Country"
          validateStatus={countryError && countryTouched ? 'error' : ''}
          help={countryError && countryTouched ? countryError : ''}
        >
          <MSelect
            placeholder="Select country"
            options={leaveTypeOptions}
            value={formik.values.country}
            onChange={(selectValue) =>
              formik.setFieldValue('country', selectValue)
            }
            onBlur={() => {
              formik.setFieldTouched('country', true);
            }}
            disabled={isApprovalForm}
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item
          label="Sponsored by"
          validateStatus={sponsoredByError && sponsoredByTouched ? 'error' : ''}
          help={sponsoredByError && sponsoredByTouched ? sponsoredByError : ''}
        >
          <MSelect
            placeholder="Select sponser"
            options={sponsorOptions}
            value={formik.values.sponsoredBy}
            onChange={(selectValue) => {
              formik.setFieldValue('sponsoredBy', selectValue);

              formik.setFieldValue('sponsoredByExtra', '');
            }}
            onBlur={() => {
              formik.setFieldTouched('sponsoredBy', true);
            }}
            disabled={isApprovalForm}
          />
        </Form.Item>
      </Col>
      {formik.values.sponsoredBy === String(SponsorTypeCodes.COMPANY) && (
        <Col span={24}>
          <Form.Item
            label="Company name"
            validateStatus={
              sponsoredByExtraError && sponsoredByExtraTouched ? 'error' : ''
            }
            help={
              sponsoredByExtraError && sponsoredByExtraTouched
                ? sponsoredByExtraError
                : ''
            }
          >
            <MSelect
              options={companyOptions}
              value={
                formik.values.sponsoredByExtra === ''
                  ? null
                  : formik.values.sponsoredByExtra
              }
              onChange={(selectValue) => {
                formik.setFieldValue('sponsoredByExtra', selectValue);
              }}
              onBlur={() => {
                formik.setFieldTouched('sponsoredByExtra', true);
              }}
              disabled={isApprovalForm}
            />
          </Form.Item>
        </Col>
      )}
      {formik.values.sponsoredBy === String(SponsorTypeCodes.SUPPLIER) && (
        <Col span={24}>
          <Form.Item
            label="Supplier Name"
            validateStatus={
              sponsoredByExtraError && sponsoredByExtraTouched ? 'error' : ''
            }
            help={
              sponsoredByExtraError && sponsoredByExtraTouched
                ? sponsoredByExtraError
                : ''
            }
          >
            <MTextArea
              name="sponsoredByExtra"
              value={formik.values.sponsoredByExtra}
              onChange={(value) => {
                formik.setFieldValue('sponsoredByExtra', value.target.value);
              }}
              onBlur={() => {
                formik.setFieldTouched('sponsoredByExtra', true);
              }}
              rows={4}
              showCount
              maxLength={100}
              disabled={isApprovalForm}
            />
          </Form.Item>
        </Col>
      )}
      {formik.values.sponsoredBy ===
        String(SponsorTypeCodes.SUB_CONTRACTOR) && (
        <Col span={24}>
          <Form.Item label="Sub-contractor Name">
            <MTextArea
              name="sponsoredByExtra"
              value={formik.values.sponsoredByExtra}
              onChange={(value) => {
                formik.setFieldValue('sponsoredByExtra', value.target.value);
              }}
              rows={4}
              showCount
              maxLength={100}
              disabled={isApprovalForm}
            />
          </Form.Item>
        </Col>
      )}
    </Row>
  );
};

export default memo(OverseasDetailsSection);
